import { useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { LINKS } from '@/data/content';

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
  </svg>
);

export default function CommunitySection() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 140, damping: 16 });
  const y = useSpring(my, { stiffness: 140, damping: 16 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - rect.left) / rect.width - 0.5) * 24);
    my.set(((e.clientY - rect.top) / rect.height - 0.5) * 24);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <section
      id="community"
      data-testid="community-section"
      className="relative overflow-hidden py-28 sm:py-36"
      aria-label="Community"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: 'radial-gradient(50% 45% at 50% 55%, rgba(0,200,255,0.12) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto flex max-w-[900px] flex-col items-center px-6 text-center sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-electric">06 — COMMUNITY</p>
          <h2 className="mt-4 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-4xl lg:text-5xl" data-testid="community-title">
            THE TOONS ARE JUST{' '}
            <span className="bg-gradient-to-r from-arc via-electric to-gold bg-clip-text text-transparent">
              GETTING STARTED.
            </span>
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-snow/65 sm:text-base" data-testid="community-copy">
            Follow the journey.
            <br />
            Meet the characters.
            <br />
            Watch the universe evolve.
          </p>
        </motion.div>

        <motion.div
          ref={ref}
          onMouseMove={onMove}
          onMouseLeave={reset}
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-14"
        >
          <motion.span
            aria-hidden="true"
            className="absolute inset-[-14px] rounded-full border border-electric/25"
            animate={{ scale: [1, 1.08, 1], opacity: [0.5, 0.15, 0.5] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.span
            aria-hidden="true"
            className="absolute inset-[-30px] rounded-full border border-electric/12"
            animate={{ scale: [1.05, 1.18, 1.05], opacity: [0.35, 0.08, 0.35] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
          />
          <motion.a
            href={LINKS.x}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="community-x-button"
            aria-label="ARC TOONS on X — @Arc_Toons"
            className="relative flex h-40 w-40 items-center justify-center rounded-full border border-electric/40 bg-navy2/80 sm:h-52 sm:w-52"
            style={{
              backdropFilter: 'blur(10px)',
              boxShadow: '0 0 60px rgba(0,200,255,0.28), inset 0 0 30px rgba(0,200,255,0.08)'
            }}
            whileHover={{ scale: 1.06, borderColor: 'rgba(0,200,255,0.9)' }}
            whileTap={{ scale: 0.97 }}
          >
            <motion.span style={{ x, y }} className="flex items-center justify-center">
              <XIcon className="h-14 w-14 text-snow sm:h-20 sm:w-20" />
            </motion.span>
          </motion.a>
        </motion.div>

        <a
          href={LINKS.x}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="community-x-handle"
          className="mt-8 font-mono text-[11px] uppercase tracking-[0.35em] text-electric/80 underline-offset-8 transition-colors hover:text-electric hover:underline"
        >
          X.COM/ARC_TOONS
        </a>
      </div>
    </section>
  );
}
